import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Ticket, Award, Calendar, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import SEO from "../components/SEO";

const Raffles = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/raffles")
            .then((r) => setRows(r.data || []))
            .catch(() => toast.error("Could not load raffles"))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="min-h-screen bg-[#FDFBF7]">
            <SEO title="Community raffles in Montana" description="View Reddog Community Foundation raffles, prize details, eligibility rules and draw dates. Support community needs in Frenchtown, Montana." path="/raffles" />
            <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
                <header className="mb-10">
                    <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3">Win something. Fund something.</p>
                    <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium tracking-tight leading-[1.05]">Charitable raffles</h1>
                    <p data-testid="raffles-payment-methods-note" className="text-base text-[#4A5568] mt-4 max-w-2xl">100% of proceeds fund the foundation. Choose <strong>online payment, cash, or check</strong> when reserving your tickets.</p>
                </header>

                {loading ? (
                    <p className="text-sm text-[#4A5568]">Loading…</p>
                ) : rows.length === 0 ? (
                    <div className="bg-white border border-black/10 rounded-2xl p-10 text-center" data-testid="raffles-empty">
                        <Ticket className="w-10 h-10 text-[#4A5568] mx-auto mb-3" strokeWidth={1.5} />
                        <p className="font-heading text-xl text-[#1C252E]">No active raffles</p>
                        <p className="text-sm text-[#4A5568] mt-2">When we open our next raffle, you&apos;ll see it here.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="raffles-list">
                        {rows.map((r) => (
                            <Link
                                key={r.id}
                                to={`/raffles/${r.slug}`}
                                data-testid={`raffle-card-${r.slug}`}
                                className="group bg-white border border-black/10 rounded-2xl overflow-hidden hover:border-[#E53935]/40 hover:shadow-md transition"
                            >
                                {r.prize_image_url && (
                                    <div
                                        data-testid={`raffle-card-image-${r.slug}`}
                                        className="aspect-[16/10] bg-[#F4F1EA] overflow-hidden"
                                    >
                                        <img
                                            src={r.prize_image_url}
                                            alt=""
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            loading="lazy"
                                        />
                                    </div>
                                )}
                                <div className="p-6">
                                <div className="flex items-center justify-between mb-3">
                                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${r.status === "open" ? "bg-green-100 text-green-800" : "bg-[#F4F1EA] text-[#4A5568]"}`}>
                                        {r.status === "open" ? "Open for entries" : (r.drawn_at ? "Drawn" : "Closed")}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">${r.ticket_price_usd}/ticket</span>
                                </div>
                                <h2 className="font-heading text-2xl text-[#1C252E] tracking-tight leading-tight group-hover:text-[#E53935] transition-colors">{r.name}</h2>
                                <p className="text-sm text-[#4A5568] mt-2 flex items-start gap-1.5">
                                    <Award className="w-4 h-4 mt-0.5 text-[#E53935] shrink-0" strokeWidth={1.5} />
                                    <span>{r.prize_description}</span>
                                </p>
                                {r.ends_at && (
                                    <p className="text-xs text-[#4A5568] mt-2 flex items-center gap-1.5">
                                        <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
                                        Drawing: {new Date(r.ends_at).toLocaleDateString(undefined, { dateStyle: "medium" })}
                                    </p>
                                )}
                                {r.winner_entry_public && (
                                    <p className="text-xs mt-3 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 text-amber-900">
                                        Winner: {r.winner_entry_public.first_name} {r.winner_entry_public.last_initial} — ticket #{r.winner_entry_public.winning_ticket_number}
                                    </p>
                                )}
                                <p className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#E53935] group-hover:gap-2 transition-all">
                                    {r.status === "open" ? "Enter raffle" : "View details"} <ArrowRight className="w-4 h-4" strokeWidth={2} />
                                </p>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Raffles;
